import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { Download, Smartphone } from 'lucide-react';
import { useInstallPrompt } from '../../hooks/usePush';
import { isInstalledApp } from '../../lib/pwa';
import {
  installNudge,
  installNudgeSnoozedAt,
  snoozeInstallNudge,
} from '../../lib/installedApp';
import { useStoreLinks } from '../../lib/siteConfig';
import { phoneStoreUrl, storeDevice } from '../../lib/storeLinks';
import { useT } from '../../i18n';

// A slim card at the top of the chats, in a browser tab only (D184): the
// installed app gets notifications on iPhone and opens full screen. Install
// when the browser can prompt, else How → the install guide. Not now hides
// it for 14 days on this device. On a phone whose store link is set, it
// offers the store app instead: Get the Twinden app (D346).
export function InstallNudge() {
  const { available, prompt } = useInstallPrompt();
  const [snoozedAt, setSnoozedAt] = useState(installNudgeSnoozedAt);
  const storeUrl = phoneStoreUrl(useStoreLinks(), storeDevice());
  const t = useT();
  const shown = installNudge({
    installed: isInstalledApp(),
    snoozedAt,
    promptAvailable: available,
    now: Date.now(),
    storeUrl,
  });
  if (!shown) return null;

  const notNow = () => {
    const now = Date.now();
    snoozeInstallNudge(now);
    setSnoozedAt(now);
  };

  return (
    <Box px={4} pb={3}>
      <HStack
        align="flex-start"
        spacing={3}
        px={4}
        py={3}
        borderRadius="18px"
        bg="accent.tint"
        role="region"
        aria-label={t(
          shown === 'store' ? 'chat.install.getApp' : 'chat.install.label'
        )}
      >
        <Box color="accent" pt="2px">
          {shown === 'store' ? (
            <Smartphone size={18} aria-hidden="true" />
          ) : (
            <Download size={18} aria-hidden="true" />
          )}
        </Box>
        <Box flex="1" minW={0}>
          {shown === 'store' ? (
            <>
              <Text fontSize="15px" fontWeight={700} lineHeight="20px">
                {t('chat.install.getApp')}
              </Text>
              <Text fontSize="14px" color="ink.body" lineHeight="19px">
                {t('chat.install.getAppBody')}
              </Text>
            </>
          ) : (
            <Text fontSize="15px" fontWeight={600} lineHeight="20px">
              {t('chat.install.body')}
            </Text>
          )}
          <HStack spacing={2} mt={2}>
            {shown === 'store' && storeUrl ? (
              <Button
                as="a"
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
              >
                {t('chat.install.openStore')}
              </Button>
            ) : shown === 'install' ? (
              <Button size="sm" onClick={() => void prompt()}>
                {t('chat.install.install')}
              </Button>
            ) : (
              <Button as={RouterLink} to="/install" size="sm">
                {t('chat.install.how')}
              </Button>
            )}
            <Button size="sm" variant="ghost" color="accent" onClick={notNow}>
              {t('chat.install.notNow')}
            </Button>
          </HStack>
        </Box>
      </HStack>
    </Box>
  );
}
